import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Share, SquarePlus, X } from 'lucide-react';
import {
  closeInstallHelp,
  dismissInstall,
  getInstallState,
  isInstallDismissed,
  requestInstall,
  subscribeInstall,
} from '../utils/installApp';

// The offer to save the site as an app (utils/installApp.js): a card at the bottom of the screen,
// shown once per browser until it is closed, and again whenever the footer's "Save as app" asks for
// the iPhone steps. The admin area and the print copy of the program never show it.

export const useInstallApp = () => {
  const [state, setState] = useState(getInstallState);
  useEffect(() => {
    const update = () => setState(getInstallState());
    update();
    return subscribeInstall(update);
  }, []);
  return state;
};

const hiddenOn = (pathname) => pathname.startsWith('/admin') || /\/program\/print\/?$/.test(pathname);

const slideUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Card = styled.aside`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 900;
  margin: 0 auto;
  max-width: 420px;
  padding: 18px 18px 16px;
  box-sizing: border-box;
  background: #060d25;
  border: 1px solid rgba(72, 171, 219, 0.45);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  color: white;
  font-family: 'Roboto', sans-serif;
  animation: ${slideUp} 0.35s ease-out;

  @media (min-width: 769px) {
    left: auto;
    right: 24px;
    bottom: 24px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Top = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding-right: 28px;
`;

const AppIcon = styled.img`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 12px;
  background: white;
`;

const Title = styled.p`
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Text = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.75);
`;

const Close = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  padding: 6px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;

  &:hover {
    color: white;
  }
`;

const Action = styled.button`
  display: block;
  width: 100%;
  margin-top: 14px;
  padding: 11px 16px;
  background: #47ABDB;
  border: none;
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #2f8fc0;
  }
`;

const Steps = styled.ol`
  margin: 14px 0 0;
  padding: 12px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  list-style: none;
  counter-reset: step;
  font-size: 0.9rem;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    counter-increment: step;
  }

  li::before {
    content: counter(step);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(72, 171, 219, 0.2);
    color: #48ABDB;
    font-size: 0.75rem;
    font-weight: 700;
  }

  svg {
    flex-shrink: 0;
    color: #48ABDB;
    vertical-align: -3px;
  }
`;

const iconProps = { size: 18, 'aria-hidden': true };

const InstallAppPrompt = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { mode, helpOpen } = useInstallApp();
  const [dismissed, setDismissed] = useState(isInstallDismissed);
  const [showSteps, setShowSteps] = useState(false);

  if (hiddenOn(pathname)) return null;
  // The footer's button reopens the iPhone steps even after the card was closed.
  if (!mode || (dismissed && !helpOpen)) return null;

  const stepsOpen = mode === 'ios' && (showSteps || helpOpen);

  const close = () => {
    dismissInstall();
    setDismissed(true);
    setShowSteps(false);
    closeInstallHelp();
  };

  return (
    <Card aria-label={intl.formatMessage({ id: 'installApp.title', defaultMessage: 'HEARTS CONFERENCE as an app' })}>
      <Close type="button" onClick={close} aria-label={intl.formatMessage({ id: 'installApp.close', defaultMessage: 'Close' })}>
        <X size={18} aria-hidden />
      </Close>
      <Top>
        <AppIcon src={`${process.env.PUBLIC_URL}/apple-touch-icon.png`} alt="" />
        <div>
          <Title><FormattedMessage id="installApp.title" defaultMessage="HEARTS CONFERENCE as an app" /></Title>
          <Text><FormattedMessage id="installApp.text" defaultMessage="Open the program and all event info with one tap from your home screen." /></Text>
        </div>
      </Top>
      {mode === 'prompt' && (
        <Action type="button" onClick={requestInstall}>
          <FormattedMessage id="installApp.install" defaultMessage="Install" />
        </Action>
      )}
      {mode === 'ios' && !stepsOpen && (
        <Action type="button" onClick={() => setShowSteps(true)}>
          <FormattedMessage id="installApp.howTo" defaultMessage="Show me how" />
        </Action>
      )}
      {stepsOpen && (
        <Steps>
          <li>
            <span>
              <FormattedMessage
                id="installApp.ios.share"
                defaultMessage="Tap {icon} “Share” in the browser bar."
                values={{ icon: <Share {...iconProps} /> }}
              />
            </span>
          </li>
          <li>
            <span>
              <FormattedMessage
                id="installApp.ios.addToHome"
                defaultMessage="Choose {icon} “Add to Home Screen”."
                values={{ icon: <SquarePlus {...iconProps} /> }}
              />
            </span>
          </li>
          <li>
            <span><FormattedMessage id="installApp.ios.confirm" defaultMessage="Tap “Add” – done." /></span>
          </li>
        </Steps>
      )}
    </Card>
  );
};

export default InstallAppPrompt;
